import { useEffect, useRef, useState } from 'react'
import { GetChatLog } from '../../services/data'
import DateTimeFormat from '../../util/DateTimeformat'
import IonIcon from '@reacticons/ionicons'
import { useLocation, useParams } from 'react-router-dom'

const Sidebar = () => {
  const [CustomerList, SetCustomerList] = useState([])
  const [Initialactive, SetInitialactive] = useState(
    localStorage.getItem('chatlog_customer_id') || ''
  )
  const [sortOption, setSortOption] = useState('newestChats')
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [showLoadMore, setShowLoadMore] = useState(false)
  const [showGoToTop, setShowGoToTop] = useState(false) // This state is for a commented-out button

  const containerRef = useRef(null)
  const chatLog = new GetChatLog()
  const location = useLocation()
  const params = useParams()
  
    // ✅ Extract store_id from URL (params or pathname)
    const getStoreIdFromUrl = () => {
      if (params?.store_id) return params.store_id
      const pathParts = location.pathname.split('/')
      console.log(pathParts[1])
      return pathParts[1] || null
    }
  
    const store_id = getStoreIdFromUrl()

  const getSortParams = () => {
    switch (sortOption) {
      case 'oldestChats':
        return { sort: 'last_message_time', order: 'asc' }
      case 'newestChats':
        return { sort: 'last_message_time', order: 'desc' }
      case 'oldestUsers':
        return { sort: 'first_message_time', order: 'asc' }
      case 'newestUsers':
        return { sort: 'first_message_time', order: 'desc' }
      default:
        return { sort: 'last_message_time', order: 'desc' }
    }
  }

  const fetchCustomers = async (pageNumber = 1, isLoadMore = false) => {
    if (isLoading) return
    setIsLoading(true)

    const storeId = store_id
    if (!storeId) {
      setIsLoading(false)
      return
    }

    const { sort, order } = getSortParams()

    try {
      const response = await chatLog.Get_Customer_List(
        storeId,
        null, // No search query
        pageNumber,
        10, // Limit per page
        sort,
        order
      )

      if (response?.success) {
        const newData = response.data || []
        const updatedList = isLoadMore ? [...CustomerList, ...newData] : newData

        SetCustomerList(updatedList) // Update the customer list

        // Determine if there's more data to load
        setHasMore(newData.length === 10)

        // If it's the first page load or a sort change, select the first item
        if (!isLoadMore && updatedList.length > 0) {
          const firstCustomer = updatedList[0]
          // Always call selectCustomer for the first item on initial load/sort change
          selectCustomer(firstCustomer)
        } else if (!isLoadMore && updatedList.length === 0) {
          // If no customers are found on a fresh load/sort, clear active selection
          SetInitialactive('')
          localStorage.removeItem('chatlog_customer_id')
          localStorage.removeItem('chatlog_customer_email')
          localStorage.removeItem('chatlog_customer_name')
          window.dispatchEvent(
            new CustomEvent('chatlog_customer_id', { detail: false }) // Indicate no active chat
          )
        }
      } else {
        // If API call fails, clear list if not loading more, and set hasMore to false
        if (!isLoadMore) SetCustomerList([])
        setHasMore(false)
      }
    } catch (error) {
      console.error('Failed to fetch customers:', error)
      if (!isLoadMore) SetCustomerList([])
      setHasMore(false)
    } finally {
      setIsLoading(false)
    }
  }

  // Helper function to set the active customer and dispatch event
  const selectCustomer = customer => {
    SetInitialactive(customer.customer_id)
    localStorage.setItem('chatlog_customer_id', customer.customer_id)
    localStorage.setItem('chatlog_customer_email', customer.email || '')
    localStorage.setItem('chatlog_customer_name', customer.name || '')
    window.dispatchEvent(
      new CustomEvent('chatlog_customer_id', { detail: true })
    )
  }

  useEffect(() => {
    // When sortOption changes, reset page to 1 and fetch customers
    setPage(1)
    setHasMore(true)
    setShowLoadMore(false) // Reset this as well
    fetchCustomers(1, false) // Fetch the first page, not a load more
  }, [sortOption])

  // Initial load of customers when component mounts
  useEffect(() => {
    // Only fetch if CustomerList is empty or if Initialactive is not set
    // to prevent redundant fetches on re-renders if data already exists
    if (CustomerList.length === 0 && !Initialactive) {
      fetchCustomers(1, false)
    } else if (Initialactive) {
      // If there's an Initialactive from localStorage, ensure the event is dispatched
      // for the MainComponent to pick up, especially on first load.
      // This helps in cases where the page refreshes and localStorage retains a value.
      window.dispatchEvent(
        new CustomEvent('chatlog_customer_id', { detail: true })
      )
    }
  }, []) // Empty dependency array means this runs once on mount

  const loadMore = () => {
    if (!isLoading && hasMore) {
      const nextPage = page + 1
      setPage(nextPage)
      fetchCustomers(nextPage, true)
    }
  }

  const resetToFirstPage = () => {
    if (page === 1 && !isLoading) return // No need to reset if already on page 1 and not loading

    setPage(1)
    setHasMore(true)
    setShowLoadMore(false)
    fetchCustomers(1, false) // Fetch first page
    if (containerRef.current) {
      containerRef.current.scrollTop = 0 // Scroll to top
    }
  }

  const handleScroll = () => {
    const container = containerRef.current
    if (!container) return

    // Show "Load More" button when near the bottom
    const nearBottom =
      container.scrollTop + container.clientHeight >=
      container.scrollHeight - 10 // 10px buffer

    setShowLoadMore(hasMore && nearBottom)

    // Show "Go To Top" button when scrolled down
    setShowGoToTop(container.scrollTop > 100)
  }

  useEffect(() => {
    const scrollEl = containerRef.current
    if (scrollEl) {
      scrollEl.addEventListener('scroll', handleScroll)
      // Cleanup listener
      return () => scrollEl.removeEventListener('scroll', handleScroll)
    }
  }, [CustomerList, hasMore]) // Re-attach listener if CustomerList or hasMore changes

  const handleActiveCustomerid = id => {
    const selectedCustomer = CustomerList.find(c => c.customer_id === id)
    if (selectedCustomer) {
      // Removed the 'Initialactive !== id' condition to always allow click.
      selectCustomer(selectedCustomer)
    }
  }

  const getRandomLightColor = id => {
    const colors = [
      '#FFD3B6',
      '#FFAAA5',
      '#FFDAA5',
      '#D4A5A5',
      '#A5D4A5',
      '#A5D4D4',
      '#A5A5D4',
      '#D4A5D4',
      '#F5CBA7',
      '#A3E4D7'
    ]
    // Use a more stable way to get a color based on ID for consistency
    const hash = id
      ? id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
      : 0
    const index = hash % colors.length
    return colors[index]
  }

  return (
    <div className='pt-8 pr-7 relative'>
      <h1 className='font-extrabold text-[23px] leading-[48px]'>Chats</h1>

      {/* Sort Dropdown */}
      <div className='mt-3 w-full'>
        <label className='text-sm font-medium mb-3 sortby inline-flex'>
          Sort By
        </label>
        <div className='relative flex items-center'>
          <select
            className='p-2 pr-10 border rounded-md text-sm w-full focus:outline-none appearance-none'
            value={sortOption}
            onChange={e => setSortOption(e.target.value)}
          >
            <option value='newestChats'>New Chats</option>
            <option value='oldestChats'>Old Chats</option>
            <option value='newestUsers'>New Users</option>
            <option value='oldestUsers'>Old Users</option>
          </select>
          <IonIcon
            name='chevron-down'
            className='absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none'
          />
        </div>
      </div>

      {/* Customer List */}
      <div className='mt-5 customerlist'>
        <div
          ref={containerRef}
          className='flex flex-col mt-6 gap-5 mb-4 max-h-[270px] overflow-y-auto scrollbar-custom1'
        >
          {CustomerList.length > 0 ? (
            CustomerList.map((item, index) => (
              <div
                key={item.customer_id || index} // Use a unique and stable key
                className={`h-[54px] hover:bg-white rounded-[8px] cursor-pointer px-[11px] py-[7px] flex items-center justify-between gap-2 whitespace-nowrap ${
                  Initialactive === item.customer_id ? 'bg-white' : ''
                }`}
                onClick={() => handleActiveCustomerid(item.customer_id)}
              >
                <div className='flex items-center gap-3'>
                  <div
                    className='w-8 h-8 rounded-full flex items-center justify-center text-white font-bold'
                    style={{
                      backgroundColor: getRandomLightColor(item.customer_id)
                    }}
                  >
                    {item.name
                      ? item.name.charAt(0).toUpperCase()
                      : item.customer_id.charAt(0).toUpperCase()}
                  </div>
                  <div className='flex flex-col gap-2'>
                    <p className='font-semibold text-[13px] text-[#444444] leading-[17.76px] w-[190px] overflow-hidden truncate'>
                      {item.name || item.customer_id}
                    </p>
                    <div className='flex gap-2'>
                      <p className='font-normal text-[11px] text-[#444444] leading-[15.03px]'>
                        {DateTimeFormat(item.last_message_time).date}
                      </p>
                      <p className='font-normal text-[11px] text-[#444444] leading-[15.03px]'>
                        {DateTimeFormat(item.last_message_time).time}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className='text-center text-gray-500'>
              {isLoading ? 'Loading customers...' : 'No customers found.'}
            </p>
          )}

          {isLoading && (
            <p className='text-sm text-center text-gray-400 py-2'>
              Loading more...
            </p>
          )}
        </div>

        {/* Load More Button */}
        {hasMore && showLoadMore && !isLoading && CustomerList.length > 0 && (
          <div className='flex justify-center mt-1 mb-1'>
            <button
              onClick={loadMore}
              className='bg-[#232328] p-2 w-full rounded-[59px] text-white text-[14px]'
            >
              Load More
            </button>
          </div>
        )}

        {/* Go To Top Button (Commented Out) */}
        {/* {showGoToTop && (
           <button
             onClick={resetToFirstPage}
             className='absolute right-10 bottom-4 w-10 h-10 flex items-center justify-center bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors shadow-lg'
             title='Go to top and refresh'
           >
             <IonIcon name='arrow-up' />
           </button>
         )} */}
      </div>
    </div>
  )
}

export default Sidebar
